// Realtime worker manager that proxies subscriptions and presence to a web worker
// Keeps callbacks and active channel tracking in-memory
type StatusCallback = (status: string, error?: string) => void;
type DataCallback = (payload: unknown) => void;
type PresenceCallback = (event: string, data: unknown) => void;

class RealtimeWorkerManager {
  private worker: Worker | null = null;
  private statusCallbacks = new Map<string, StatusCallback>();
  private dataCallbacks = new Map<string, DataCallback>();
  private presenceCallbacks = new Map<string, PresenceCallback>();
  private activeChannels = new Set<string>();

  initialize() {
    if (this.worker) return;

    this.worker = new Worker(
      new URL("../workers/realtime.worker.ts", import.meta.url),
      { type: "module" },
    );

    this.worker.onmessage = (e: MessageEvent) => {
      const message = e.data;

      if (message.type === "status") {
        const callback = this.statusCallbacks.get(message.channelId);
        if (callback) {
          callback(message.status, message.error);
        }
      } else if (message.type === "data") {
        const callback = this.dataCallbacks.get(message.channelId);
        if (callback) {
          callback(message.payload);
        }
      } else if (message.type === "presence") {
        const callback = this.presenceCallbacks.get(message.channelId);
        if (callback) {
          callback(message.event, message);
        }
      }
    };

    this.worker.onerror = (error) => {
      console.error("🔴 Realtime worker error:", error);
    };
  }

  setAuth(accessToken: string) {
    if (!this.worker) this.initialize();
    this.worker?.postMessage({
      type: "setAuth",
      accessToken,
    });
  }

  subscribe(
    channelId: string,
    config: {
      event: string;
      schema: string;
      table: string;
      filter?: string;
    },
    onData: DataCallback,
    onStatus: StatusCallback,
    accessToken?: string,
  ) {
    if (!this.worker) this.initialize();

    if (this.activeChannels.has(channelId)) {
      return;
    }

    this.dataCallbacks.set(channelId, onData);
    this.statusCallbacks.set(channelId, onStatus);
    this.activeChannels.add(channelId);

    this.worker?.postMessage({
      type: "subscribe",
      channelId,
      config,
      accessToken,
    });
  }

  subscribePresence(
    channelId: string,
    onPresence: PresenceCallback,
    onStatus: StatusCallback,
    accessToken?: string,
    presenceKey?: string,
  ) {
    if (!this.worker) this.initialize();

    if (this.activeChannels.has(channelId)) {
      return;
    }

    this.presenceCallbacks.set(channelId, onPresence);
    this.statusCallbacks.set(channelId, onStatus);
    this.activeChannels.add(channelId);

    // Send presenceKey to the worker so it can create the channel with presence config
    // We include it both on the top-level message and inside config for backward/forward compatibility
    this.worker?.postMessage({
      type: "subscribe",
      channelId,
      config: {
        event: "presence",
        schema: "",
        table: "",
        presenceKey,
      },
      accessToken,
      presenceKey,
    });
  }

  subscribeHousehold(
    channelId: string,
    tables: Array<{ table: string; filter: string }>,
    onData: DataCallback,
    onPresence: PresenceCallback | null,
    onStatus: StatusCallback,
    accessToken?: string,
    presenceKey?: string,
  ) {
    if (!this.worker) this.initialize();

    if (this.activeChannels.has(channelId)) return;

    this.dataCallbacks.set(channelId, onData);
    this.statusCallbacks.set(channelId, onStatus);
    if (onPresence) {
      this.presenceCallbacks.set(channelId, onPresence);
    }
    this.activeChannels.add(channelId);

    this.worker?.postMessage({
      type: "subscribeHousehold",
      channelId,
      tables,
      accessToken,
      presenceKey,
    });
  }

  trackPresence(channelId: string, payload: Record<string, unknown>) {
    if (!this.worker) return;

    this.worker.postMessage({
      type: "trackPresence",
      channelId,
      payload,
    });
  }

  unsubscribe(channelId: string) {
    if (!this.worker) return;

    this.worker.postMessage({
      type: "unsubscribe",
      channelId,
    });

    this.dataCallbacks.delete(channelId);
    this.statusCallbacks.delete(channelId);
    this.presenceCallbacks.delete(channelId);
    this.activeChannels.delete(channelId);
  }

  terminate() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
      this.statusCallbacks.clear();
      this.dataCallbacks.clear();
      this.presenceCallbacks.clear();
      this.activeChannels.clear();
    }
  }
}

export const realtimeWorker = new RealtimeWorkerManager();
